import { useState, useEffect } from "react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Check, Crown, Zap, Star, Loader2, Settings, AlertCircle } from "lucide-react";
import { useAuth, authFetch } from "../context/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const tiers = [
  {
    id: "free",
    name: "Starter",
    description: "Get started for free",
    priceMonthly: 0,
    priceYearly: 0,
    icon: Zap,
    features: [
      "Up to 10 wardrobe items",
      "Daily outfit uploads",
      "Basic cost-per-wear tracking",
      "Basic wardrobe insights",
      "Monthly 'My Wardrobe Report' (limited)"
    ],
    limitations: []
  },
  {
    id: "plus",
    name: "WornWell Plus",
    description: "For the fashion-conscious",
    priceMonthly: 7.99,
    priceYearly: 59,
    icon: Star,
    recommended: true,
    features: [
      "Unlimited wardrobe items",
      "Full cost-per-wear analytics",
      "Monthly My Wardrobe Report",
      "Seasonal reports (Spring/Summer, Fall/Winter)",
      "'Rediscover' recommendations",
      "'Most worn' insights",
      "'Style profile' insights",
      "Outfit upload streaks"
    ],
    limitations: []
  },
  {
    id: "pro",
    name: "WornWell Pro",
    description: "For power users",
    priceMonthly: 14.99,
    priceYearly: 99,
    icon: Crown,
    features: [
      "Everything in Plus",
      "Resale value estimates (Depop, Grailed, etc.)",
      "One-tap resale listing tools",
      "'Most expensive mistake' insights",
      "Smart buying recommendations",
      "Wardrobe value tracking",
      "Priority new features",
      "Advanced analytics"
    ],
    limitations: []
  }
];

export function Subscription() {
  const { user, updateUser } = useAuth();
  const [currentTier, setCurrentTier] = useState("free");
  const [billingCycle, setBillingCycle] = useState("yearly");
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [managingSubscription, setManagingSubscription] = useState(false);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/subscription/status`);
      if (response.ok) {
        const data = await response.json();
        setCurrentTier(data.tier || "free");
        setSubscriptionStatus(data.subscription);
        
        // Set billing cycle from current subscription if exists
        if (data.subscription?.billing_cycle) {
          setBillingCycle(data.subscription.billing_cycle);
        }
      }
    } catch (error) {
      console.error("Failed to fetch subscription status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tierId) => {
    if (tierId === "free" || tierId === currentTier) return;
    
    setCheckoutLoading(tierId);
    
    try {
      const response = await authFetch(`${API_URL}/api/subscription/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tier: tierId,
          billing_cycle: billingCycle,
          origin_url: window.location.origin
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create checkout session");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    setManagingSubscription(true);
    
    try {
      const response = await authFetch(`${API_URL}/api/subscription/manage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          origin_url: window.location.origin
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to open subscription management");
      }
    } catch (error) {
      console.error("Manage subscription error:", error);
      toast.error("Failed to open subscription management");
    } finally {
      setManagingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm("Are you sure you want to cancel your subscription? You'll retain access until the end of your billing period.")) {
      return;
    }
    
    try {
      const response = await authFetch(`${API_URL}/api/subscription/cancel`, {
        method: "POST"
      });
      
      if (response.ok) {
        toast.success("Subscription will be canceled at the end of your billing period");
        fetchSubscriptionStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to cancel subscription");
      }
    } catch (error) {
      console.error("Cancel error:", error);
      toast.error("Failed to cancel subscription");
    }
  };

  const handleReactivate = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/subscription/reactivate`, {
        method: "POST"
      });
      
      if (response.ok) {
        toast.success("Subscription reactivated!");
        fetchSubscriptionStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to reactivate subscription");
      }
    } catch (error) {
      console.error("Reactivate error:", error);
      toast.error("Failed to reactivate subscription");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const getButtonText = (tierId) => {
    if (tierId === currentTier) return "Current Plan";
    if (tierId === "free") return "Downgrade";
    
    const tierOrder = ["free", "plus", "pro"];
    const currentIndex = tierOrder.indexOf(currentTier);
    const targetIndex = tierOrder.indexOf(tierId);
    
    return targetIndex > currentIndex ? "Upgrade" : "Downgrade";
  };

  const getPrice = (tier) => {
    if (billingCycle === "yearly") {
      const monthlyEquivalent = (tier.priceYearly / 12).toFixed(2);
      return `$${monthlyEquivalent}`;
    }
    return `$${tier.priceMonthly.toFixed(2)}`;
  };

  const getSavings = (tier) => {
    if (tier.priceYearly === 0) return null;
    const yearlyCost = tier.priceYearly;
    const monthlyCost = tier.priceMonthly * 12;
    const savings = Math.round(((monthlyCost - yearlyCost) / monthlyCost) * 100);
    return savings > 0 ? savings : null;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 max-w-5xl mx-auto" data-testid="subscription-page">
        {/* Header */}
        <div className="text-center">
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight text-primary mb-2">
            Choose Your Plan
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Unlock the full potential of your wardrobe with WornWell
          </p>
        </div>

        {/* Current Subscription Status */}
        {subscriptionStatus && subscriptionStatus.tier !== "free" && (
          <Card className="border border-border bg-sand-50">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">Current Plan:</span>
                    <Badge className="bg-primary text-primary-foreground">
                      {tiers.find(t => t.id === currentTier)?.name || currentTier}
                    </Badge>
                    {subscriptionStatus.status === "canceling" && (
                      <Badge variant="outline" className="text-orange-600 border-orange-300">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Cancels {formatDate(subscriptionStatus.current_period_end)}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {subscriptionStatus.billing_cycle === "yearly" ? "Annual" : "Monthly"} billing
                    {subscriptionStatus.current_period_end && (
                      <> • Renews {formatDate(subscriptionStatus.current_period_end)}</>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  {subscriptionStatus.status === "canceling" ? (
                    <Button variant="outline" size="sm" onClick={handleReactivate}>
                      Reactivate
                    </Button>
                  ) : (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleManageSubscription}
                        disabled={managingSubscription}
                      >
                        {managingSubscription ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Settings className="w-4 h-4 mr-2" />
                        )}
                        Manage
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleCancelSubscription}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Billing Toggle */}
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-3 p-1 bg-sand-100 rounded-full">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                billingCycle === "monthly"
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("yearly")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                billingCycle === "yearly"
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Annual
              <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                Save up to 38%
              </Badge>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {tiers.map((tier) => {
            const Icon = tier.icon;
            const isCurrentTier = tier.id === currentTier;
            const savings = getSavings(tier);
            
            return (
              <Card 
                key={tier.id} 
                className={`relative border ${
                  tier.recommended 
                    ? "border-primary shadow-lg" 
                    : "border-border"
                } ${isCurrentTier ? "ring-2 ring-primary ring-offset-2" : ""}`}
              >
                {tier.recommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground">
                      Most Popular
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="p-6 pb-4 text-center">
                  <div className="mx-auto mb-3 p-3 bg-sand-100 rounded-full w-fit">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle className="font-heading text-xl">{tier.name}</CardTitle>
                  <p className="text-sm text-muted-foreground">{tier.description}</p>
                </CardHeader>
                
                <CardContent className="p-6 pt-0">
                  <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold">{getPrice(tier)}</span>
                      <span className="text-muted-foreground">/mo</span>
                    </div>
                    {billingCycle === "yearly" && tier.priceYearly > 0 && (
                      <p className="text-sm text-muted-foreground mt-1">
                        ${tier.priceYearly} billed annually
                        {savings && (
                          <span className="text-green-600 ml-1">
                            (Save {savings}%)
                          </span>
                        )}
                      </p>
                    )}
                    {tier.priceMonthly === 0 && (
                      <p className="text-sm text-muted-foreground mt-1">Free forever</p>
                    )}
                  </div>
                  
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button
                    className={`w-full ${
                      tier.recommended && !isCurrentTier
                        ? "bg-primary hover:bg-primary/90"
                        : ""
                    }`}
                    variant={isCurrentTier ? "outline" : tier.recommended ? "default" : "outline"}
                    onClick={() => handleSubscribe(tier.id)}
                    disabled={isCurrentTier || tier.id === "free" || checkoutLoading === tier.id}
                  >
                    {checkoutLoading === tier.id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    {getButtonText(tier.id)}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* FAQ or Additional Info */}
        <Card className="border border-border">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg mb-4">Frequently Asked Questions</h3>
            <div className="space-y-4 text-sm">
              <div>
                <p className="font-medium mb-1">Can I change my plan anytime?</p>
                <p className="text-muted-foreground">
                  Yes! You can upgrade, downgrade, or cancel your subscription at any time. Changes take effect at your next billing cycle.
                </p>
              </div>
              <div>
                <p className="font-medium mb-1">What payment methods do you accept?</p>
                <p className="text-muted-foreground">
                  We accept all major credit and debit cards via Stripe, including Visa, Mastercard, and American Express.
                </p>
              </div>
              <div>
                <p className="font-medium mb-1">Is my payment information secure?</p>
                <p className="text-muted-foreground">
                  Absolutely. All payments are processed securely through Stripe. We never store your card details.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

export default Subscription;
